import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import _merge from 'lodash/merge';

import store from 'client/redux/store';
import { initAppState } from 'client/redux/store';
import { setInited, setUserStateInited } from 'client/redux/modules/flags';
import {
    useRemoveUnnecessaryQueryStr,
    validateTestCode
} from 'client/hooks/useInitPromo/handleQueryStringTest';
import getPromoInfoData from 'client/hooks/useInitPromo/getPromoInfoData';
import getUgcRelatedState from 'ugc/hooks/useInitData/getUgcRelatedState';
import getPromoState from 'client/hooks/useInitPromo/getPromoState';
import getUserLoginState from 'client/hooks/useInitPromo/userLoginState/getUserLoginState';
import getUserEntryState from 'client/hooks/useInitPromo/userEntryState/getUserEntryState';
import {
    handleInitError,
    setupPromoApp,
    retrieveStateFromLocalStorage
} from 'client/hooks/useInitPromo/useInitPromo';
import useHistoryPathPush from 'client/hooks/useHistoryPathPush';
import useInitAbortHelper from 'client/hooks/useInitAbortHelper';
import { setGameResultRecordsAndWinnerList } from 'client/hooks/useInitPromo/getGameResultRecordsAndWinnerList';

function useInitUgc(paramsPromoId) {
    const dispatch = useDispatch();
    const history = useHistoryPathPush();
    const removeUnnecessaryQueryStr = useRemoveUnnecessaryQueryStr();
    const abortHelper = useInitAbortHelper();

    function toNotFound() {
        history.replace({ pathname: '/notfound' });
    }

    async function initUgc(recaptchaToken) {
        let state = {};

        function mergeAndDispatchState(source) {
            state = _merge({}, state, source);
            dispatch(initAppState(source));
        }

        try {
            const promoInfo = await getPromoInfoData(paramsPromoId, ['mgmRelation', 'pointPool']);
            const isContestUgcModule = promoInfo.relatedTwinsPromo !== undefined;
            const gameType = promoInfo.promo.type;
            store.injectGameReducer(gameType, isContestUgcModule);
            await setupPromoApp(promoInfo);
            mergeAndDispatchState(promoInfo);

            const { rest: qs, retrieved } = removeUnnecessaryQueryStr(state);
            mergeAndDispatchState({
                appState: { hasTryLiff: Boolean(retrieved.tryliff) }
            });

            retrieveStateFromLocalStorage(mergeAndDispatchState);

            const promoState = getPromoState(state, qs);
            mergeAndDispatchState({ promoState });

            const ugcRelatedState = getUgcRelatedState(state, isContestUgcModule);
            mergeAndDispatchState(ugcRelatedState);

            dispatch(setInited());

            if (isContestUgcModule) {
                await validateTestCode(state, qs);

                const userLoginState = await getUserLoginState(state);
                mergeAndDispatchState({ userLoginState });

                if (userLoginState.isLogin) {
                    await setGameResultRecordsAndWinnerList(state, mergeAndDispatchState);

                    const userEntryState = await getUserEntryState({
                        state,
                        dispatch,
                        abortHelper,
                        recaptchaToken,
                        enableQualificationPasscode:
                            promoInfo?.settingPromo?.enableQualificationPasscode,
                        enableReCaptcha: promoInfo?.settingPromo?.enableReCaptcha
                    });
                    mergeAndDispatchState({ userEntryState });
                }
            }

            await store.initGameReducer(gameType);
            dispatch(setUserStateInited());
        } catch (error) {
            if (await abortHelper.current.canContinue()) {
                handleInitError({
                    error,
                    dispatch,
                    toNotFound,
                    isTestMode: state?.promoState?.isTestMode
                });
            }
        }
    }

    useEffect(() => {
        initUgc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return recaptchaToken => initUgc(recaptchaToken);
}

export default useInitUgc;
